import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import axios from 'axios';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import AudioPlayer from './components/AudioPlayer';
import Home from './pages/Home';
import Browse from './pages/Browse';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import CheckoutSuccess from './pages/CheckoutSuccess';
import Dashboard from './pages/Dashboard';
import Wishlist from './pages/Wishlist';
import AdminDashboard from './pages/AdminDashboard';
import AdminProducts from './pages/AdminProducts';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-xl text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Main App Content
const AppContent = () => {
    const [currentAudio, setCurrentAudio] = useState(null);
    const [cartCount, setCartCount] = useState(0);
    const { isAuthenticated } = useAuth();

    useEffect(() => {
        // Seed database on first load
        seedDatabase();
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            fetchCartCount();
        } else {
            setCartCount(0);
        }
    }, [isAuthenticated]);

    const seedDatabase = async () => {
        try {
            await axios.post(`${API}/seed`);
        } catch (error) {
            console.log('Database already seeded or error seeding');
        }
    };

    const fetchCartCount = async () => {
        try {
            const response = await axios.get(`${API}/cart`);
            setCartCount(response.data.items?.length || 0);
        } catch (error) {
            console.error('Error fetching cart count:', error);
        }
    };

    return (
        <>
            <Navbar cartCount={cartCount} />
            <Routes>
                <Route path="/" element={<Home setCurrentAudio={setCurrentAudio} />} />
                <Route path="/browse" element={<Browse setCurrentAudio={setCurrentAudio} />} />
                <Route path="/sample-packs" element={<Browse setCurrentAudio={setCurrentAudio} />} />
                <Route path="/songs" element={<Browse setCurrentAudio={setCurrentAudio} />} />
                <Route path="/albums" element={<Browse setCurrentAudio={setCurrentAudio} />} />
                <Route path="/product/:productId" element={<ProductDetail setCurrentAudio={setCurrentAudio} />} />
                <Route 
                    path="/cart" 
                    element={
                        <ProtectedRoute>
                            <Cart />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="/checkout" 
                    element={
                        <ProtectedRoute>
                            <Checkout />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="/checkout/success" 
                    element={
                        <ProtectedRoute>
                            <CheckoutSuccess />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="/dashboard" 
                    element={
                        <ProtectedRoute>
                            <Dashboard />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="/wishlist" 
                    element={
                        <ProtectedRoute>
                            <Wishlist setCurrentAudio={setCurrentAudio} />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="/admin" 
                    element={
                        <ProtectedRoute>
                            <AdminDashboard />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="/admin/products" 
                    element={
                        <ProtectedRoute>
                            <AdminProducts />
                        </ProtectedRoute>
                    } 
                />
            </Routes>
            {currentAudio && (
                <AudioPlayer 
                    audioUrl={currentAudio.url}
                    productName={currentAudio.name}
                    onClose={() => setCurrentAudio(null)}
                />
            )}
            <Toaster position="top-right" theme="dark" />
        </>
    );
};

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppContent />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
